/**
 * Dice + check resolution — ported from RLGame.roll_dice / set_check_rate.
 *
 * Rules (identical to the original):
 *  - Roll 2d6 plus optional extra dice paid with luck.
 *  - The *first two* dice decide criticals: double-6 = critical success,
 *    double-1 = critical failure (regardless of extra dice).
 *  - Otherwise total = sum(dice) + currentPoints; success if total >= required.
 */

import type { CheckOutcome } from "./types.js";
import type { Rng } from "./rng.js";

export interface CheckResult {
  dice: number[];
  /** Sum of the dice (ignored on a critical). */
  diceTotal: number;
  crit: "none" | "success" | "fail";
  /** diceTotal + currentPoints (only meaningful when crit === "none"). */
  total: number;
  outcome: CheckOutcome;
  currentPoints: number;
  requiredPoints: number;
}

export function rollDice(numDice: number, rng: Rng): number[] {
  const dice: number[] = [];
  for (let i = 0; i < numDice; i++) dice.push(1 + Math.floor(rng() * 6));
  return dice;
}

export function resolveCheck(
  dice: number[],
  currentPoints: number,
  requiredPoints: number,
): CheckResult {
  const [d0, d1] = dice;
  const diceTotal = dice.reduce((a, b) => a + b, 0);

  if (d0 === 6 && d1 === 6) {
    return {
      dice,
      diceTotal,
      crit: "success",
      total: Infinity,
      outcome: 3,
      currentPoints,
      requiredPoints,
    };
  }
  if (d0 === 1 && d1 === 1) {
    return {
      dice,
      diceTotal,
      crit: "fail",
      total: -Infinity,
      outcome: 0,
      currentPoints,
      requiredPoints,
    };
  }
  const total = diceTotal + currentPoints;
  return {
    dice,
    diceTotal,
    crit: "none",
    total,
    outcome: total >= requiredPoints ? 2 : 1,
    currentPoints,
    requiredPoints,
  };
}

/** Roll and resolve in one step. */
export function performCheck(
  numDice: number,
  currentPoints: number,
  requiredPoints: number,
  rng: Rng,
): CheckResult {
  return resolveCheck(rollDice(numDice, rng), currentPoints, requiredPoints);
}

const sumDistributionCache = new Map<number, number[]>();

function sumDistribution(numDice: number): number[] {
  const cached = sumDistributionCache.get(numDice);
  if (cached) return cached;
  let dist = [1];
  for (let i = 0; i < numDice; i++) {
    const next = Array(dist.length + 6).fill(0);
    for (let sum = 0; sum < dist.length; sum++) {
      for (let d = 1; d <= 6; d++) next[sum + d] += dist[sum];
    }
    dist = next;
  }
  sumDistributionCache.set(numDice, dist);
  return dist;
}

function countSumsAtLeast(numDice: number, threshold: number): number {
  if (numDice <= 0) return threshold <= 0 ? 1 : 0;
  const dist = sumDistribution(numDice);
  let count = 0;
  for (let sum = Math.max(0, threshold); sum < dist.length; sum++) count += dist[sum];
  return count;
}

/** Number of favourable outcomes when you still need `needed` more points. */
export function successCount(needed: number, numDice: number): number {
  numDice = Math.max(2, Math.trunc(numDice) || 2);
  const total = Math.pow(6, numDice);
  let c = 0;
  const restDice = numDice - 2;
  const restOutcomes = Math.pow(6, restDice);
  for (let d0 = 1; d0 <= 6; d0++) {
    for (let d1 = 1; d1 <= 6; d1++) {
      if (d0 === 6 && d1 === 6) {
        c += restOutcomes;
      } else if (d0 !== 1 || d1 !== 1) {
        c += countSumsAtLeast(restDice, needed - d0 - d1);
      }
    }
  }
  if (c < total / 36 || needed > numDice * 6) c = total / 36;
  else if (c > (total * 35) / 36) c = (total * 35) / 36;
  return c;
}

/** Probability in [0,1] of passing a check given the current/required points. */
export function successProbability(
  currentPoints: number,
  requiredPoints: number,
  numDice = 2,
): number {
  const needed = Math.round(requiredPoints - currentPoints);
  return successCount(needed, numDice) / Math.pow(6, numDice);
}
